<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { required, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import SlaTimeInput from './SlaTimeInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
  submitLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close', 'submitSla']);

const SLA_PLAN_TRANSLATION_MAP = {
  'SLA Plan - Basic': 'SLA.PLANS.BASIC.NAME',
  'Basic response service level agreement': 'SLA.PLANS.BASIC.DESCRIPTION',
  'SLA Plan - Standard': 'SLA.PLANS.STANDARD.NAME',
  'Standard response service level agreement': 'SLA.PLANS.STANDARD.DESCRIPTION',
  'SLA Plan - Pro': 'SLA.PLANS.PRO.NAME',
  'Pro fast response service level agreement': 'SLA.PLANS.PRO.DESCRIPTION',
  'SLA Plan - Premium': 'SLA.PLANS.PREMIUM.NAME',
  'Premium ultra-fast response service level agreement':
    'SLA.PLANS.PREMIUM.DESCRIPTION',
};

const { t } = useI18n();
const uiFlags = useMapGetter('sla/getUIFlags');

const name = ref('');
const description = ref('');
const onlyDuringBusinessHours = ref(false);
const active = ref(true);
const slaTimeInputsValidation = ref({});
const slaTimeInputs = reactive([
  {
    threshold: null,
    unit: 'Minutes',
    label: 'SLA.FORM.FIRST_RESPONSE_TIME.LABEL',
    placeholder: 'SLA.FORM.FIRST_RESPONSE_TIME.PLACEHOLDER',
  },
  {
    threshold: null,
    unit: 'Minutes',
    label: 'SLA.FORM.RESOLUTION_TIME.LABEL',
    placeholder: 'SLA.FORM.RESOLUTION_TIME.PLACEHOLDER',
  },
]);

const rules = {
  name: {
    required,
    minLength: minLength(2),
  },
};

const v$ = useVuelidate(rules, { name });

const isSlaTimeInputsInvalid = computed(() =>
  Object.values(slaTimeInputsValidation.value).some(Boolean)
);

const isSubmitDisabled = computed(
  () =>
    v$.value.name.$invalid ||
    isSlaTimeInputsInvalid.value ||
    uiFlags.value.isUpdating
);
const submitIcon = computed(() =>
  props.selectedResponse?.id ? 'i-lucide-refresh-cw' : 'i-lucide-plus'
);

const translatedSlaTimeInputs = computed(() => [
  {
    ...slaTimeInputs[0],
    label: t('SLA.FORM.FIRST_RESPONSE_TIME.LABEL'),
    placeholder: t('SLA.FORM.FIRST_RESPONSE_TIME.PLACEHOLDER'),
  },
  {
    ...slaTimeInputs[1],
    label: t('SLA.FORM.RESOLUTION_TIME.LABEL'),
    placeholder: t('SLA.FORM.RESOLUTION_TIME.PLACEHOLDER'),
  },
]);

const slaNameErrorMessage = computed(() => {
  if (!v$.value.name.$error) return '';
  if (v$.value.name.required?.$invalid) {
    return t('SLA.FORM.NAME.REQUIRED_ERROR');
  }
  if (v$.value.name.minLength?.$invalid)
    return t('SLA.FORM.NAME.MINIMUM_LENGTH_ERROR');

  return '';
});

const translateSlaValue = value => {
  switch (SLA_PLAN_TRANSLATION_MAP[value]) {
    case 'SLA.PLANS.BASIC.NAME':
      return t('SLA.PLANS.BASIC.NAME');
    case 'SLA.PLANS.BASIC.DESCRIPTION':
      return t('SLA.PLANS.BASIC.DESCRIPTION');
    case 'SLA.PLANS.STANDARD.NAME':
      return t('SLA.PLANS.STANDARD.NAME');
    case 'SLA.PLANS.STANDARD.DESCRIPTION':
      return t('SLA.PLANS.STANDARD.DESCRIPTION');
    case 'SLA.PLANS.PRO.NAME':
      return t('SLA.PLANS.PRO.NAME');
    case 'SLA.PLANS.PRO.DESCRIPTION':
      return t('SLA.PLANS.PRO.DESCRIPTION');
    case 'SLA.PLANS.PREMIUM.NAME':
      return t('SLA.PLANS.PREMIUM.NAME');
    case 'SLA.PLANS.PREMIUM.DESCRIPTION':
      return t('SLA.PLANS.PREMIUM.DESCRIPTION');
    default:
      return value;
  }
};

const setFormValues = () => {
  const {
    name: selectedName,
    description: selectedDescription,
    first_response_time_threshold: firstResponseTimeThreshold,
    resolution_time_threshold: resolutionTimeThreshold,
    only_during_business_hours: selectedOnlyDuringBusinessHours,
    active: selectedActive,
  } = props.selectedResponse;

  name.value = translateSlaValue(selectedName);
  description.value = translateSlaValue(selectedDescription);
  onlyDuringBusinessHours.value = selectedOnlyDuringBusinessHours;
  active.value = selectedActive !== undefined ? selectedActive : true;

  [firstResponseTimeThreshold, resolutionTimeThreshold].forEach(
    (threshold, index) => {
      const converted = convertSecondsToTimeUnit(threshold, {
        minute: 'Minutes',
        hour: 'Hours',
        day: 'Days',
      });
      slaTimeInputs[index].threshold = converted.time;
      slaTimeInputs[index].unit = converted.unit;
    }
  );
};

const updateThreshold = (index, value) => {
  slaTimeInputs[index].threshold = value;
};

const updateUnit = (index, unit) => {
  slaTimeInputs[index].unit = unit;
};

const convertToSeconds = index => {
  const { threshold, unit } = slaTimeInputs[index];
  if (threshold === null || threshold === 0) return null;

  const unitsToSeconds = { Minutes: 60, Hours: 3600, Days: 86400 };
  return Number(threshold * (unitsToSeconds[unit] || 1));
};

const handleIsInvalid = (index, isInvalid) => {
  slaTimeInputsValidation.value = {
    ...slaTimeInputsValidation.value,
    [index]: isInvalid,
  };
};

const onSubmit = () => {
  emit('submitSla', {
    name: name.value,
    description: description.value,
    first_response_time_threshold: convertToSeconds(0),
    resolution_time_threshold: convertToSeconds(1),
    only_during_business_hours: onlyDuringBusinessHours.value,
    active: active.value,
  });
};

onMounted(() => {
  if (props.selectedResponse && Object.keys(props.selectedResponse).length) {
    setFormValues();
  }
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <form class="flex flex-wrap mx-0" @submit.prevent="onSubmit">
      <woot-input
        v-model="name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :label="$t('SLA.FORM.NAME.LABEL')"
        :placeholder="$t('SLA.FORM.NAME.PLACEHOLDER')"
        :error="slaNameErrorMessage"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('SLA.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('SLA.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <SlaTimeInput
        v-for="(input, index) in translatedSlaTimeInputs"
        :key="input.label"
        :threshold="input.threshold"
        :threshold-unit="input.unit"
        :label="input.label"
        :placeholder="input.placeholder"
        @update-threshold="updateThreshold(index, $event)"
        @unit="updateUnit(index, $event)"
        @is-in-valid="handleIsInvalid(index, $event)"
      />

      <div
        class="mt-3 flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-lg justify-between"
      >
        <span class="text-n-slate-11">
          {{ $t('SLA.FORM.BUSINESS_HOURS.PLACEHOLDER') }}
        </span>
        <ToggleSwitch id="sla_bh" v-model="onlyDuringBusinessHours" />
      </div>

      <div
        class="mt-3 flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-lg justify-between"
      >
        <span class="text-n-slate-11">
          {{ $t('SLA.FORM.ACTIVE.PLACEHOLDER') }}
        </span>
        <ToggleSwitch id="sla_active" v-model="active" />
      </div>

      <div class="flex items-center justify-end w-full gap-2 mt-8">
        <NextButton
          faded
          slate
          type="reset"
          icon="i-lucide-x"
          :label="$t('SLA.FORM.CANCEL')"
          @click.prevent="emit('close')"
        />
        <NextButton
          type="submit"
          :icon="submitIcon"
          :label="submitLabel"
          :disabled="isSubmitDisabled"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
